/**
 * Service de sécurité pour les headers HTTP
 * Gère les signatures HMAC et validations de timestamp
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

const ALGORITHM = { name: 'HMAC', hash: 'SHA-256' };
const MAX_VALIDITY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Crée une signature HMAC-SHA256 pour une configuration
 * Côté frontend : utilise MASTER_KEY depuis l'environnement ou localStorage
 * Côté worker : utilise MASTER_KEY depuis env
 * 
 * @param {Object} routingData - Configuration de routage à signer
 * @param {string} masterKey - Clé maître pour la signature
 * @returns {Promise<string>} Signature encodée en base64
 */
export async function createSignature(routingData, masterKey) {
  try {
    // Normaliser l'objet pour une signature cohérente
    const normalizedData = JSON.stringify(routingData);
    
    // Convertir la clé en Uint8Array
    const encoder = new TextEncoder();
    const keyData = encoder.encode(masterKey);
    
    // Importer la clé
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      ALGORITHM,
      false,
      ['sign']
    );
    
    // Créer la signature
    const signatureBuffer = await crypto.subtle.sign(
      ALGORITHM.name,
      cryptoKey,
      encoder.encode(normalizedData)
    );
    
    // Convertir en base64
    const signatureArray = Array.from(new Uint8Array(signatureBuffer));
    const signatureBase64 = btoa(String.fromCharCode(...signatureArray));
    
    debug.log(config, 'Signature HMAC créée');
    return signatureBase64;
  } catch (error) {
    debug.error(config, 'Erreur lors de la création de la signature HMAC:', error);
    throw new Error(`Signature creation failed: ${error.message}`);
  }
}

/**
 * Valide une signature HMAC-SHA256
 * @param {Object} routingData - Configuration de routage
 * @param {string} signature - Signature en base64 à valider
 * @param {string} masterKey - Clé maître pour valider la signature
 * @returns {Promise<boolean>} true si la signature est valide
 */
export async function validateSignature(routingData, signature, masterKey) {
  try {
    // Créer la signature attendue
    const expectedSignature = await createSignature(routingData, masterKey);
    
    // Comparaison en temps constant (prévention timing attacks)
    const isValid = constantTimeCompare(signature, expectedSignature);
    
    if (!isValid) {
      debug.warn(config, 'Signature invalide');
    }
    
    return isValid;
  } catch (error) {
    debug.error(config, 'Erreur lors de la validation de la signature:', error);
    return false;
  }
}

/**
 * Comparaison en temps constant pour prévenir les timing attacks
 * @param {string} a - Première chaîne
 * @param {string} b - Deuxième chaîne
 * @returns {boolean}
 */
function constantTimeCompare(a, b) {
  if (a.length !== b.length) return false;
  
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  
  return result === 0;
}

/**
 * Valide le timestamp d'une configuration
 * @param {number} timestamp - Timestamp en millisecondes
 * @param {number} maxValidityMs - Durée de validité maximale (défaut: 5 minutes)
 * @returns {boolean} true si le timestamp est valide
 */
export function validateTimestamp(timestamp, maxValidityMs = MAX_VALIDITY_MS) {
  const now = Date.now();
  const age = now - timestamp;
  
  if (age < 0) {
    // Timestamp dans le futur (horloge décalée)
    debug.warn(config, 'Timestamp dans le futur détecté');
    return false;
  }
  
  if (age > maxValidityMs) {
    debug.warn(config, `Timestamp expiré (${age}ms > ${maxValidityMs}ms)`);
    return false;
  }
  
  return true;
}

/**
 * Complète une configuration avec métadonnées de sécurité
 * Utilisé côté frontend avant envoi des headers
 * 
 * @param {Object} routingConfig - Configuration de routage
 * @param {string} masterKey - Clé maître pour créer la signature
 * @returns {Promise<Object>} Configuration enrichie avec _security
 */
export async function enrichConfigWithSecurity(routingConfig, masterKey) {
  try {
    const signature = await createSignature(routingConfig, masterKey);
    
    return {
      ...routingConfig,
      _security: {
        signature,
        timestamp: routingConfig._metadata.timestamp,
        algorithm: 'HMAC-SHA256'
      }
    };
  } catch (error) {
    debug.error(config, 'Erreur lors de l\'enrichissement de sécurité:', error);
    return routingConfig;
  }
}

/**
 * Valide la sécurité d'une configuration reçue
 * Utilisé côté worker pour valider les headers
 * 
 * @param {Object} routingConfig - Configuration reçue
 * @param {string} masterKey - Clé maître
 * @returns {Promise<{isValid: boolean, errors: string[]}>}
 */
export async function validateConfigSecurity(routingConfig, masterKey) {
  const errors = [];
  
  // Vérifier signature
  if (routingConfig._security && routingConfig._security.signature) {
    const isSignatureValid = await validateSignature(
      { ...routingConfig, _security: undefined },
      routingConfig._security.signature,
      masterKey
    );
    
    if (!isSignatureValid) {
      errors.push('Invalid signature');
    }
  } else {
    errors.push('Missing signature');
  }
  
  // Vérifier timestamp
  if (routingConfig._metadata && routingConfig._metadata.timestamp) {
    const isTimestampValid = validateTimestamp(routingConfig._metadata.timestamp);
    
    if (!isTimestampValid) {
      errors.push('Expired timestamp');
    }
  } else {
    errors.push('Missing timestamp');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

export default {
  createSignature,
  validateSignature,
  validateTimestamp,
  enrichConfigWithSecurity,
  validateConfigSecurity,
  ALGORITHM,
  MAX_VALIDITY_MS
};
